import { ApiAreaOfFocus } from 'api/types/ApiAreaOfFocus';
import { DetailsList } from 'components/DetailsList/DetailsList';
import { DetailsListItem } from 'components/DetailsList/Item/DetailsListItem';
import { ApiJob } from 'modules/listing/api/job/types/ApiJob';
import { AREAS_OF_FOCUS } from 'utils/constants/general/areasOfFocus';
import { humanDate } from 'utils/date';
import { isDefined } from 'utils/functional';
import { getEducationLevelName, getProfessionalLevelName } from 'utils/job';
import { formatSalaryRangeWithPeriodHumanName } from 'utils/listings';

// TODO: reference the remaining ApiJob fields
type Props = {
  orgAreasOfFocus?: ApiAreaOfFocus[];
  applicationDeadline: string | null;
  education: string;
  salaryMinimum: number | null;
  salaryMaximum: number | null;
  salaryCurrency: string | null;
  salaryPeriod: string | null;
  salaryDetails: string | null;
  professionalLevel: string;
  jobCountry: string;
} & Pick<
  ApiJob,
  'startDate' | 'endDate' | 'isFullTime' | 'isTemporary' | 'isContract'
>;

export function JobDetailsList({
  orgAreasOfFocus,
  education,
  isContract,
  isFullTime,
  isTemporary,
  startDate,
  endDate,
  applicationDeadline,
  professionalLevel,
  salaryCurrency,
  salaryDetails,
  salaryMaximum,
  salaryMinimum,
  salaryPeriod,
  jobCountry,
}: Props) {
  return (
    <DetailsList>
      {isFullTime && !isContract && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="clock"
          value={getText('Full Time')}
        />
      )}

      {!isFullTime && !isContract && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="part-pie"
          value={getText('Part Time')}
        />
      )}

      {isContract && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="paper"
          value={getText('Contract / Freelance')}
        />
      )}

      {isTemporary && (
        <DetailsListItem
          title={getText('Job Type')}
          iconName="hourglass"
          value={getText('Temporary')}
        />
      )}

      {startDate && (
        <DetailsListItem
          title={getText('Start Date')}
          iconName="calendar"
          value={humanDate(startDate)}
        />
      )}

      {endDate && (
        <DetailsListItem
          title={getText('End Date')}
          iconName="calendar"
          value={humanDate(endDate)}
        />
      )}

      {applicationDeadline && (
        <DetailsListItem
          title={getText('Application Deadline')}
          iconName="hourglass"
          value={humanDate(applicationDeadline)}
        />
      )}

      {education !== 'NO_REQUIREMENT' && (
        <DetailsListItem
          title={getText('Education')}
          iconName="graduation-cap"
          value={getEducationLevelName(education)}
        />
      )}

      {professionalLevel !== 'NONE' && (
        <DetailsListItem
          title={getText('Experience Level')}
          iconName="stairs"
          value={getProfessionalLevelName(professionalLevel)}
        />
      )}

      {(Number(salaryMinimum) ||
        Number(salaryMaximum) ||
        isDefined(salaryDetails)) && (
        <DetailsListItem
          title={
            salaryPeriod === 'YEAR'
              ? getText('Salary')
              : getText('Compensation')
          }
          qaId="listing-compensation"
          iconName="money"
          tooltipText={getText(
            'Idealist encourages all organizations to include salary ranges when possible. Please consider this range a "best estimate" rather than a firm or final offer.',
          )}
          value={
            isDefined(salaryMinimum) || isDefined(salaryMaximum)
              ? formatSalaryRangeWithPeriodHumanName(
                  salaryMinimum,
                  salaryMaximum,
                  jobCountry,
                  salaryCurrency,
                  // @ts-expect-error TS(2345): Argument of type 'string | null | undefined' is no... Remove this comment to see the full error message
                  salaryPeriod,
                )
              : getText('See below')
          }
          additionalText={salaryDetails}
        />
      )}

      {orgAreasOfFocus?.length !== 0 && (
        <DetailsListItem
          iconName="globe"
          title={getText('Areas of Focus')}
          value={orgAreasOfFocus
            ?.map((item) => AREAS_OF_FOCUS[item])
            .join(', ')}
        />
      )}
    </DetailsList>
  );
}
